import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { UserProfile, CreateUserData, UpdateUserData } from './user.type';

const isTestMode = process.env.NODE_ENV === 'development';
const USERS_COLLECTION = isTestMode ? 'web_app_test_users' : 'users';

export const createUserProfile = async (uid: string, userData: CreateUserData): Promise<UserProfile> => {
  const userDoc = doc(db, USERS_COLLECTION, uid);
  const profileData: UserProfile = {
    uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...userData,
  };

  await setDoc(userDoc, profileData);
  return profileData;
};

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const userDoc = doc(db, USERS_COLLECTION, uid);
  const docSnap = await getDoc(userDoc);
  return docSnap.exists() ? docSnap.data() as UserProfile : null;
};

const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  delay = 1000,
): Promise<T> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      const isNetworkError = error?.message?.includes('ERR_QUIC_PROTOCOL_ERROR') ||
                            error?.code === 'unavailable' ||
                            error?.code === 'timeout';

      if (attempt === maxRetries || !isNetworkError) {
        throw error;
      }

      const currentDelay = delay;
      await new Promise(resolve => setTimeout(resolve, currentDelay));
      delay *= 2; // Exponential backoff
    }
  }
  throw new Error('Max retries exceeded');
};

export const updateUserProfileInDatabase = async (uid: string, updates: UpdateUserData): Promise<void> => {
  const userDoc = doc(db, USERS_COLLECTION, uid);

  await retryOperation(
    () => updateDoc(userDoc, {
      ...updates,
      updatedAt: serverTimestamp(),
    }),
    3, // 3 retries
    1000, // Start with 1s delay
  );
};
